import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../../components/layout';
import { Alert } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { type Columns, DataTable } from '../../components/ui/data-table';
import { Input, Select } from '../../components/ui/input';
import { api, errorMessage } from '../../lib/api';
import type { OrderRow } from '../../lib/types';
import { formatDateTime, formatMoney } from '../../lib/utils';

export const ORDER_STATUSES = [
  'NEW',
  'PAYMENT_PENDING',
  'PAID',
  'RESERVED',
  'PROCESSING',
  'PACKED',
  'SHIPPED',
  'DELIVERED',
  'RETURN_REQUESTED',
  'RETURNED',
  'REFUNDED',
  'COMPLETED',
  'CANCELLED',
] as const;

const PAGE = 50;

export function statusVariant(status: string) {
  if (status === 'CANCELLED' || status === 'REFUNDED') return 'destructive' as const;
  if (status === 'COMPLETED' || status === 'DELIVERED') return 'success' as const;
  if (status === 'PAID' || status === 'RETURN_REQUESTED') return 'warning' as const;
  return 'secondary' as const;
}

export function OrderListPage() {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState('');
  const [market, setMarket] = useState('');
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(0);
  const params = new URLSearchParams({ limit: String(PAGE), offset: String(page * PAGE) });
  if (status) params.set('status', status);
  if (market) params.set('market', market);
  if (q.trim()) params.set('q', q.trim());
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const list = useQuery({
    queryKey: ['orders', params.toString()],
    queryFn: () => api<{ items: OrderRow[]; total: number }>(`/orders?${params.toString()}`),
  });
  const reset = () => {
    setPage(0);
  };

  const columns = useMemo<Columns<OrderRow>>(
    () => [
      {
        id: 'number',
        header: t('ord.number'),
        accessorFn: (r) => r.number,
        cell: ({ row }) => (
          <Link
            to="/orders/$orderId"
            params={{ orderId: row.original.id }}
            className="font-medium hover:underline"
          >
            {row.original.number}
          </Link>
        ),
      },
      {
        id: 'created',
        header: t('ord.created'),
        accessorFn: (r) => r.createdAt,
        cell: ({ row }) => formatDateTime(row.original.createdAt, i18n.language),
      },
      { id: 'customer', header: t('ord.customer'), accessorFn: (r) => `${r.name} · ${r.email}` },
      { id: 'market', header: t('ord.market'), accessorFn: (r) => r.marketCode },
      {
        id: 'status',
        header: t('po.status'),
        accessorFn: (r) => r.status,
        cell: ({ row }) => (
          <Badge variant={statusVariant(row.original.status)}>
            {t(`ord.statuses.${row.original.status as 'NEW'}`)}
          </Badge>
        ),
      },
      {
        id: 'payment',
        header: t('ord.payment'),
        accessorFn: (r) => r.paymentStatus,
        cell: ({ row }) => t(`ord.paymentStatuses.${row.original.paymentStatus as 'paid'}`),
      },
      {
        id: 'total',
        header: t('po.total'),
        accessorFn: (r) => r.totalMinor,
        cell: ({ row }) =>
          formatMoney(row.original.totalMinor, row.original.currency, i18n.language),
      },
    ],
    [t, i18n.language],
  );

  const total = list.data?.total ?? 0;
  return (
    <>
      <PageHeader title={t('ord.title')} description={t('ord.hint')} />
      <div className="mb-4 flex flex-wrap gap-2">
        <Input
          aria-label={t('ord.search')}
          placeholder={t('ord.search')}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            reset();
          }}
          className="w-64"
        />
        <Select
          aria-label={t('po.status')}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            reset();
          }}
          className="w-48"
        >
          <option value="">{t('po.allStatuses')}</option>
          {ORDER_STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`ord.statuses.${s}`)}
            </option>
          ))}
        </Select>
        <Select
          aria-label={t('ord.market')}
          value={market}
          onChange={(e) => {
            setMarket(e.target.value);
            reset();
          }}
          className="w-32"
        >
          <option value="">{t('ord.allMarkets')}</option>
          {['LV', 'LT', 'EE'].map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </Select>
        <Input
          type="date"
          aria-label={t('ord.from')}
          value={from}
          onChange={(e) => {
            setFrom(e.target.value);
            reset();
          }}
          className="w-40"
        />
        <Input
          type="date"
          aria-label={t('ord.to')}
          value={to}
          onChange={(e) => {
            setTo(e.target.value);
            reset();
          }}
          className="w-40"
        />
      </div>
      {list.error ? (
        <Alert variant="destructive" className="mb-4">
          {errorMessage(list.error)}
        </Alert>
      ) : null}
      <DataTable
        columns={columns}
        data={list.data?.items ?? []}
        loading={list.isPending}
        rowKey={(r) => r.id}
      />
      <div className="mt-3 flex items-center justify-between text-sm text-muted-foreground">
        <span>{t('ord.found', { count: total })}</span>
        <span className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={page === 0}
            onClick={() => {
              setPage((p) => p - 1);
            }}
          >
            ←
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={(page + 1) * PAGE >= total}
            onClick={() => {
              setPage((p) => p + 1);
            }}
          >
            →
          </Button>
        </span>
      </div>
    </>
  );
}
